import Cookies from 'universal-cookie';
import { store } from '@/store/store'
import { appSetVersion, appSetMessage } from '@/store/app'
import { authIsNotLoggedIn, authHasNoAccess } from '@/store/auth'
import { captureException } from '../utils/sentry';

const cookies = new Cookies();
const appUrl = '/';

export const request = async (
  uri: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  body?: Record<string, any>,
  responseType: "json" | "blob" = "json"
): Promise<{ success: boolean; data?: any; headers?: Headers; error?: string; pagination?: any }> => {
  try {
    const options: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: responseType === "blob" ? "application/octet-stream" : "application/json",
      },
    };

    if (body && (method === "POST" || method === "PUT")) {
      options.body = JSON.stringify(body);
    }

    const token = uri.includes("admin/")
      ? cookies.get("admin-gls-token")
      : cookies.get("gls-token");

    if (token) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    const response = await fetch(appUrl + uri, options);

    if (response.ok) {
      const headers = response.headers; // Extract headers
      if (responseType === "blob") {
        const blob = await response.blob();
        return { success: true, data: blob, headers };
      }

      const result = await response.json();
      
      if (result.messages && Array.isArray(result.messages)) {
        messageHandler(result.messages); // Process and display success messages
      }

      return { success: true, data: result.data || null, pagination: result.pagination || null, headers };
    }

    // Error handling (unauthorized, forbidden, etc.)
    if (response.status === 401) {
      store.dispatch(authIsNotLoggedIn());
    } else if (response.status === 403) {
      store.dispatch(authHasNoAccess());
    }

    const result = await response.json();
    const extractedErrors = extractErrors(result);
    messageHandler(extractedErrors);

    // Capture API errors in Sentry
    captureException(new Error(`API Error: ${response.status} - ${result.message || 'Unknown error'}`), {
      uri,
      method,
      status: response.status,
      response: result,
    });

    return {
      success: false,
      error: extractedErrors.map((err) => err.text).join("; "),
    };
  } catch (err) {
    // Capture network errors in Sentry
    captureException(err as Error, {
      uri,
      method,
      body,
    });
    
    messageHandler([{ text: "An unexpected error occurred", type: "error" }]);
    return { success: false, error: "An unexpected error occurred" };
  }
};

// Message handler
const messageHandler = (messages: any) => {
  if (messages && messages.length) {
    for (const message of messages) {
      if (message.text === 'Unauthenticated.') {
        continue;
      }
      store.dispatch(appSetMessage(message));
    }
  }
};

/**
 * Extracts errors from different formats returned by the API.
 */
function extractErrors(errorResponse: any): Array<{ text: string; type: string }> {
  if (!errorResponse) return [{ text: 'Unknown error occurred', type: 'error' }];

  if (typeof errorResponse === 'string') {
    return [{ text: errorResponse, type: 'error' }];
  }

  if (Array.isArray(errorResponse)) {
    return errorResponse.map((err) => ({
      text: err.text || err.message || 'Unknown error',
      type: err.type || 'error',
    }));
  }

  if (typeof errorResponse === 'object') {
    // First, handle errors with the "errors" key
    if (errorResponse.errors) {
      return Object.values(errorResponse.errors)
        .flat()
        .map((msg) => ({ text: String(msg), type: 'error' })); // Cast msg to string
    }

    // Check for messages array at the root level
    if (errorResponse.messages && Array.isArray(errorResponse.messages)) {
      return errorResponse.messages.map((msg: any) => ({
        text: msg.text || 'Unknown error',
        type: msg.type || 'error',
      }));
    }

    // Handle single message errors (fallback)
    if (errorResponse.message) {
      return [{ text: errorResponse.message, type: 'error' }];
    }

    // Specific Stripe or nested exceptions
    if (errorResponse.exception && errorResponse.message) {
      return [
        {
          text: `${errorResponse.message}`,
          type: 'error',
        },
      ];
    }
  }

  return [{ text: 'An unexpected error occurred', type: 'error' }];
}